import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { MessageCircle, Send, CheckCircle2, Reply, ThumbsUp, ClipboardCheck } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { dispatchTimelineFocus } from './timelineFocusController';
import { toast } from 'sonner';

/**
 * CardDiscussion
 * - Hover comment icon on a timeline card / inject.
 * - Plan Discussion Group members + admins can comment (threaded) and approve the card.
 * - Comments + approvals are locked once the exercise is activated (Requested stage only).
 */
export const CardDiscussion = ({ incident, itemId, currentUser, label = 'card', alwaysVisible = false, addedLive = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [participantCount, setParticipantCount] = useState(0);
  const [canParticipate, setCanParticipate] = useState(false);
  const [canAct, setCanAct] = useState(false);
  const [message, setMessage] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);
  const [obsTitle, setObsTitle] = useState('');
  const [obsNote, setObsNote] = useState('');
  const [savingObs, setSavingObs] = useState(false);

  // Active stage: this card's "Discussion & Approval" becomes a direct "Corrective Actions & AAR"
  // observation-capture entry point. The observation then shows up in the Corrective Actions widget.
  // Real incidents get the same capture affordance while they are operational
  // (any non-closed status) — there is no discussion/approval stage for them.
  const incidentStatus = String(incident?.status || '').toLowerCase();
  const isRealIncident = !incident?.is_exercise && !incident?.is_scenario;
  const isActiveStage = isRealIncident
    ? !['resolved', 'closed', 'completed', 'cancelled', 'false_alarm'].includes(incidentStatus)
    : (!!incident?.is_exercise && incidentStatus === 'active');

  const handleSaveObservation = async () => {
    const t = obsTitle.trim();
    if (!t) { toast.error('Observation title required'); return; }
    setSavingObs(true);
    try {
      const res = await CommanServices.createObservation(incidentId, { title: t, note: obsNote.trim(), source_type: 'manual', source_comment_id: null });
      if (res?.status !== false) {
        toast.success('Observation captured');
        setObsTitle('');
        setObsNote('');
        setOpen(false);
        window.dispatchEvent(new CustomEvent('corrective-actions-refresh'));
      } else {
        toast.error(res?.message || 'Failed to save observation');
      }
    } catch (e) {
      toast.error('Failed to save observation');
    } finally {
      setSavingObs(false);
    }
  };

  const incidentId = incident?.id;
  const myId = String(currentUser?.id || currentUser?._id || '');
  const iApproved = approvals.some((a) => String(a.user_id) === myId);

  const load = useCallback(async () => {
    // Real incidents use this control purely for observation capture — no
    // discussion thread to load.
    if (!incidentId || !itemId || isRealIncident) return;
    setLoading(true);
    try {
      const res = await CommanServices.getTimelineComments(incidentId, itemId);
      const d = res?.data ?? res;
      setComments(d?.comments || []);
      setApprovals(d?.approvals || []);
      setParticipantCount(d?.participant_count || 0);
      setCanParticipate(!!d?.can_participate);
      setCanAct(!!d?.can_act);
    } catch (e) {
      console.error('load comments failed', e);
    } finally {
      setLoading(false);
    }
  }, [incidentId, itemId, isRealIncident]);

  useEffect(() => {
    load();
  }, [load]);

  // Open this card's discussion when the activity feed (banner) requests it.
  useEffect(() => {
    const handler = (e) => {
      if (String(e?.detail?.itemId || '') === String(itemId || '')
        && (!e?.detail?.incidentId || String(e.detail.incidentId) === String(incidentId))) {
        if (!e?.detail?.skipTimelineFocus) {
          dispatchTimelineFocus({
            incidentId,
            itemId,
            reason: 'discussion-selection',
            pauseAfterFocus: true,
          });
        }
        setOpen(true);
      }
    };
    window.addEventListener('open-card-discussion', handler);
    return () => window.removeEventListener('open-card-discussion', handler);
  }, [incidentId, itemId]);

  const handlePost = async () => {
    const msg = message.trim();
    if (!msg) return;
    setPosting(true);
    try {
      const res = await CommanServices.addTimelineComment(incidentId, itemId, { message: msg, parent_id: replyTo });
      if (res?.status !== false) {
        setMessage('');
        setReplyTo(null);
        load();
        window.dispatchEvent(new CustomEvent('refresh-activity-feed'));
      } else {
        toast.error(res?.message || 'Failed to post comment');
      }
    } catch (e) {
      toast.error('Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleApprove = async () => {
    try {
      const res = await CommanServices.toggleTimelineApproval(incidentId, itemId);
      if (res?.status !== false) {
        const d = res?.data ?? res;
        setApprovals(d?.approvals || []);
        toast.success(d?.approved ? 'Card approved' : 'Approval removed');
        // May have auto-advanced the exercise to "approved" — refresh the incident header.
        window.dispatchEvent(new CustomEvent('card-approval-changed'));
      } else {
        toast.error(res?.message || 'Failed to update approval');
      }
    } catch (e) {
      toast.error('Failed to update approval');
    }
  };

  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesOf = (id) => comments.filter((c) => c.parent_id === id);
  const totalComments = comments.length;
  const hasComments = totalComments > 0;
  const fullyApproved = participantCount > 0 && approvals.length >= participantCount;
  const triggerColor = fullyApproved
    ? 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600'
    : hasComments
    ? 'border-red-300 bg-red-50 text-red-600 hover:border-red-400 hover:text-red-700'
    : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600';

  // Real incidents only carry the capture affordance while operational — the
  // exercise discussion/approval mode never applies to them.
  if (isRealIncident && !isActiveStage) return null;

  return (
    <>
      {addedLive && (
        <span
          data-testid={`card-added-live-${itemId}`}
          className="inline-flex items-center gap-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold mr-1"
          title="This card was added manually during the live exercise"
        >
          Added live
        </span>
      )}
      <button
        type="button"
        data-testid={`card-discussion-trigger-${itemId}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={isActiveStage ? 'Capture observation — Corrective Actions & AAR' : 'Discuss & approve this card'}
        className={`${alwaysVisible || isActiveStage ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity inline-flex items-center gap-1 rounded-full border ${isActiveStage ? 'border-indigo-200 bg-indigo-50 text-indigo-600 hover:border-indigo-300' : triggerColor} px-2 py-0.5 text-[11px] shadow-sm`}
      >
        {isActiveStage ? (
          <ClipboardCheck className="h-3.5 w-3.5" />
        ) : (
          <>
            <MessageCircle className="h-3.5 w-3.5" />
            {totalComments > 0 && <span className="font-semibold">{totalComments}</span>}
            <span className={`inline-flex items-center gap-0.5 font-semibold ${fullyApproved ? 'text-white' : 'text-emerald-600'}`}>
              <CheckCircle2 className="h-3 w-3" />{approvals.length}/{participantCount || 0}
            </span>
          </>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`card-discussion-modal-${itemId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {isActiveStage
                ? <><ClipboardCheck className="h-4 w-4 text-indigo-500" /> Corrective Actions &amp; AAR</>
                : <><MessageCircle className="h-4 w-4 text-indigo-500" /> Discussion &amp; Approval</>}
            </DialogTitle>
          </DialogHeader>

          {isActiveStage ? (
            <div className="space-y-3" data-testid={`card-observation-form-${itemId}`}>
              <p className="text-xs text-gray-500">
                Capture an observation for this card. It appears in the <b>Corrective Actions &amp; AAR</b> widget, where you complete the corrective action.
              </p>
              <input
                data-testid="observation-card-title"
                value={obsTitle}
                onChange={(e) => setObsTitle(e.target.value)}
                placeholder="Observation title (what happened / what to improve)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <textarea
                data-testid="observation-card-note"
                value={obsNote}
                onChange={(e) => setObsNote(e.target.value)}
                placeholder="Details / lesson learned (optional)"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
              />
              <Button
                size="sm"
                data-testid="observation-card-save"
                onClick={handleSaveObservation}
                disabled={savingObs || !obsTitle.trim()}
                className="w-full h-9 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <ClipboardCheck className="h-4 w-4 mr-1" /> Save observation
              </Button>
            </div>
          ) : (
          <>
          {/* Approval summary */}
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <ThumbsUp className="h-4 w-4 text-emerald-600" />
              <span className="font-bold text-gray-900" data-testid="approval-count">
                {approvals.length}/{participantCount || 0}
              </span>
              <span className="text-gray-500">approved</span>
            </div>
            {canParticipate && canAct && (
              <Button
                size="sm"
                data-testid="approve-card-btn"
                onClick={handleApprove}
                className={iApproved
                  ? 'h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'h-8 text-xs bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50'}
                variant={iApproved ? 'default' : 'outline'}
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {iApproved ? 'Approved' : 'Approve'}
              </Button>
            )}
          </div>
          {approvals.length > 0 && (
            <div className="flex flex-wrap gap-1.5 -mt-1">
              {approvals.map((a) => (
                <span key={a.user_id} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] text-emerald-700">
                  <CheckCircle2 className="h-2.5 w-2.5" /> {a.user_name}
                </span>
              ))}
            </div>
          )}

          {/* Comment thread */}
          <div className="max-h-72 overflow-y-auto space-y-3 pr-1" data-testid="comment-thread">
            {loading ? (
              <p className="text-xs text-gray-400 italic py-4 text-center">Loading…</p>
            ) : topLevel.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-4 text-center">No comments yet. Start the discussion.</p>
            ) : (
              topLevel.map((c) => (
                <div key={c.id} className="space-y-2">
                  <CommentBubble c={c} onReply={canParticipate && canAct ? () => setReplyTo(c.id) : null} />
                  {repliesOf(c.id).length > 0 && (
                    <div className="ml-5 space-y-2 border-l-2 border-gray-100 pl-3">
                      {repliesOf(c.id).map((r) => (
                        <CommentBubble key={r.id} c={r} />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Composer */}
          {canParticipate && canAct ? (
            <div className="space-y-2 border-t border-gray-100 pt-3">
              {replyTo && (
                <div className="flex items-center justify-between text-[11px] text-indigo-600 bg-indigo-50 rounded px-2 py-1">
                  <span className="flex items-center gap-1"><Reply className="h-3 w-3" /> Replying to a comment</span>
                  <button type="button" onClick={() => setReplyTo(null)} className="font-bold hover:underline">Cancel</button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="comment-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
                  placeholder="Add a comment…"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[40px] max-h-28 resize-y"
                />
                <Button
                  size="sm"
                  data-testid="comment-send-btn"
                  onClick={handlePost}
                  disabled={posting || !message.trim()}
                  className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 italic border-t border-gray-100 pt-3">
              {canParticipate
                ? 'Comments are locked because the exercise is no longer at the Requested stage.'
                : 'Only plan discussion group members can comment here.'}
            </p>
          )}
          </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const CommentBubble = ({ c, onReply }) => (
  <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-gray-800">{c.user_name}</span>
      <span className="text-[10px] text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
    </div>
    <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{c.message}</p>
    {onReply && (
      <button type="button" onClick={onReply} className="mt-1 text-[11px] text-indigo-500 hover:text-indigo-700 inline-flex items-center gap-1">
        <Reply className="h-3 w-3" /> Reply
      </button>
    )}
  </div>
);

export default CardDiscussion;
